import type { ClawTutorialFocus, LabTutorialFocus, SilkTutorialFocus, SortingLevel, TileTutorialFocus, TowerTutorialFocus, TutorialFocus } from "@nano/content";
import type { AvatarId, Equipment, LevelProof } from "@nano/contracts";
import type { HouseId } from "@nano/game-config";

/**
 * The only channel between React (menus, HUD) and Phaser (world, puzzle).
 * Neither side keeps its own copy of the save; they exchange intents.
 */
export interface BridgeEvents {
  // React -> Phaser
  "input:block": boolean;
  "avatar:set": { avatarId: AvatarId; equipped: Equipment; reaction?: "try-on" | "celebrate" };
  "city:spotlight": HouseId | null;
  "city:houses": void;
  /** CSS px the city HUD nav covers at the bottom, so the map can scroll clear of it. */
  "city:bottom-inset": number;
  "puzzle:start": { level: SortingLevel };
  "puzzle:command": "undo" | "hint" | "restart" | "pause" | "resume" | "quit";
  "puzzle:tutorial-focus": TutorialFocus | null;
  "settings:dpr": number;
  /** CSS px a puzzle scene keeps free at the bottom (the companion corner). */
  "layout:bottom-inset": number;

  // Phaser -> React
  "boot:progress": number;
  "boot:ready": void;
  "city:house-tapped": HouseId;
  "city:arrived": HouseId;
  "puzzle:ready": void;
  "puzzle:state": {
    actions: number;
    maxMoves: number | null;
    targetMoves: number;
    canUndo: boolean;
    itemsLeft: number;
    hints: number;
  };
  "puzzle:won": {
    proof: LevelProof;
    stars: 1 | 2 | 3;
    hints: number;
    timeMs: number | null;
    bestCombo: number;
    /** Color Lab: pours in the line the player kept. */
    pours?: number;
    /** Tháp Bi Lật: drops in the line the player kept. */
    drops?: number;
    /** Máy Gắp Lém Lỉnh: coins in the line the player kept. */
    coins?: number;
    /** Xưởng Phép Thuật: rolls in the line the player kept. */
    rolls?: number;
    /** Lối Ngọc: most tiles the satchel held at once along the line the player kept. */
    peak?: number;
  };
  /** A correct placement; `combo` counts placements since the last mistake or undo. */
  "puzzle:place": { combo: number; binFull: boolean };
  "puzzle:out-of-moves": void;
  "puzzle:feedback": "mistake" | "undo" | "hint" | "win" | "pick" | "phase";
  "puzzle:hint": "drop" | "undo" | "none";
  "puzzle:first-move": void;
  "game:context-lost": void;

  // Color Lab (Phaser -> React, and the tutorial highlight React -> Phaser)
  "lab:state": { pours: number; canUndo: boolean; hints: number; stuck: boolean };
  "lab:tutorial-focus": LabTutorialFocus | null;
  /** Where the vials stand (CSS px), so the tutorial bubble can sit beside them, not on them. */
  "lab:board": { top: number; bottom: number };

  // Lối Ngọc (Phaser -> React, and the tutorial highlight React -> Phaser)
  "jade:state": { left: number; peak: number; canUndo: boolean; hints: number; stuck: boolean };
  "jade:tutorial-focus": TileTutorialFocus | null;

  // Tháp Bi Lật (Phaser -> React, and the tutorial highlight React -> Phaser)
  "tower:state": { drops: number; canUndo: boolean; hints: number };
  "tower:tutorial-focus": TowerTutorialFocus | null;
  /** Where the tower stands (CSS px), so the tutorial bubble can sit clear of it. */
  "tower:board": { top: number; bottom: number };

  // Xưởng Phép Thuật (Phaser -> React, and the tutorial highlight React -> Phaser)
  "silk:state": { rolls: number; canUndo: boolean; hints: number; stuck: boolean };
  "silk:tutorial-focus": SilkTutorialFocus | null;

  // Máy Gắp Lém Lỉnh (Phaser -> React, and the tutorial highlight React -> Phaser)
  "claw:state": { coins: number; canUndo: boolean; hints: number; stuck: boolean };
  "claw:tutorial-focus": ClawTutorialFocus | null;
  /** Where the machine stands (CSS px), so the tutorial bubble can sit clear of it. */
  "claw:board": { top: number; bottom: number };
}

type Handler<T> = (payload: T) => void;

/** Events whose latest value late subscribers (e.g. a scene still loading) must see. */
const STICKY: ReadonlySet<keyof BridgeEvents> = new Set(["input:block", "city:bottom-inset", "layout:bottom-inset", "lab:board", "tower:board", "claw:board"]);

class Bridge {
  private handlers = new Map<keyof BridgeEvents, Set<Handler<never>>>();
  private latestValues = new Map<keyof BridgeEvents, unknown>();

  latest<K extends keyof BridgeEvents>(event: K): BridgeEvents[K] | undefined {
    return this.latestValues.get(event) as BridgeEvents[K] | undefined;
  }

  on<K extends keyof BridgeEvents>(event: K, handler: Handler<BridgeEvents[K]>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as Handler<never>);
    return () => set.delete(handler as Handler<never>);
  }

  emit<K extends keyof BridgeEvents>(event: K, ...payload: BridgeEvents[K] extends void ? [] : [BridgeEvents[K]]): void {
    if (STICKY.has(event)) this.latestValues.set(event, payload[0]);
    for (const handler of this.handlers.get(event) ?? []) {
      (handler as Handler<BridgeEvents[K] | undefined>)(payload[0]);
    }
  }
}

export const bridge = new Bridge();

// Singletons (store, services, listeners) must never be duplicated by a hot update.
if (import.meta.hot) import.meta.hot.accept(() => window.location.reload());
